// alternative-components/compactWidgetsCardDetails/ApprovalRequiredCompactWidget.tsx
import { useEffect, useRef, useState } from "react";
import { t } from "i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import { gxIsArray } from "@/app/utils/helpers";
import { useDebounce } from "@/app/hooks/useDebounce";
import { gxDashboardPendingApprovalViewSummaryApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import { gxPaymentPendingViewSummaryApi } from "@/app/apiConfig/services/paymentsAbstractApiLayer";
import { ApproverCell } from "@/app/components/common/table/KendoCustomCells";
import type {
  DashboardPendingApprovalViewSummaryPayload,
  DashboardPendingApprovalViewSummaryResponse,
} from "@/app/types/alterNativeDashboardLayout";
import type {
  ApprovalRequiredTransactions,
  TransactionState,
} from "@/app/types/payments";
import type { GridRowClickEvent } from "@progress/kendo-react-grid";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import NoDataFound from "@/app/components/common/warning/NoDataFound";
import { DrawerNoFooter } from "@/app/models/drawer/DrawerNoFooter";
import { RemarksModal } from "@/app/models/dialogs/RemarksModal";
import SuccessModalSM from "@/app/models/dialogs/SuccessModalSM";
import ErrorModal from "../../../payments/initiateANewTransfer/ErrorModal";
import { DetailsComponent } from "../../../Dashboard/PaymentDetails/DetailsComponent";
import ViewAllApprovalRequired from "../../../Dashboard/transactions/ViewAllApprovalRequired";
import BeneficiaryApproval from "../../../Dashboard/BeneficiaryApproval/BeneficiaryApproval";
import RtpPaymentsApproval from "../../../Dashboard/transactions/RtpPaymentsApproval";
import BulkUploadApprove from "../../../bulk-uploads/bulk-uploadApprove/BulkUploadApprove";
import BulkUploadApprovePayroll from "../../../bulk-uploads/bulk-uploadApprovePayroll/BulkUploadApprovePayroll";
import { usePaymentApprovalFlow } from "../../types/usePaymentApprovalFlow";
import { useT } from "@/app/hooks/useT";
import { Constants } from "@/app/utils/AppConstants";
import "./styles/CompactWidgets.scss";
import "../../../Dashboard/transactions/style/RepairTransaction.scss";
import ApprovalRequiredReviewAllModal from "./ApprovalRequiredReviewAllModal";
import type { ApprovalCardItem } from "../../types/approval";

interface ApprovalRequiredCompactWidgetProps {
  title?: string;
  onReviewAll?: () => void;
  access?: { view: boolean; approve: boolean };
}

const COMPACT_WIDGET_VISIBLE_COUNT = 2;
const PENDING_APPROVAL_STATUS = "4415";

const REVIEW_ALL_PAGE_SIZE = 50;

const initialTransactionState: TransactionState = {
  isFirstTimeApprovalReqiredCall: true,
  openRtpApprovalRequired: false,
  openSfrView: false,
};

const mapToApprovalCardItem = (
  item: DashboardPendingApprovalViewSummaryResponse,
): ApprovalCardItem =>
  ({
    id: item.recordId,
    tabId: "all",
    transId: item.recordId,
    debitBankName: item.bankName ?? "",
    beneNickname: item.accountName ?? "",
    requestedDate: item.paymentDate,
    referenceNumber: item.gxChnNo,
    amount: Number(item.debitAmount) || 0,
    currencySymbol: item.debitCurrency ?? "",
    numberOfApproved: item.noOfApproved,
    numberOfLevels: item.noOfApprLevel,
    raw: item,
    accountName: item.accountName ?? "",
    bankName: item.bankName ?? "",
    recordId: item.recordId,
    statusCode: item.statusCode,
    paymentDate: item.paymentDate,
    debitAmount: item.debitAmount,
    debitCurrency: item.debitCurrency,
    gxChnNo: item.gxChnNo,
    noOfApprLevel: item.noOfApprLevel,
    noOfApproved: item.noOfApproved,
  }) as ApprovalCardItem;

const ApprovalRequiredCompactWidget = ({
  onReviewAll,
  access = { view: true, approve: true },
}: ApprovalRequiredCompactWidgetProps) => {
  const tHook = useT();
  const router = useNavigate();

  const [items, setItems] = useState<
    DashboardPendingApprovalViewSummaryResponse[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [tableList, setTableList] = useState<ApprovalCardItem[]>([]);
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
  const [isReviewAllOpen, setIsReviewAllOpen] = useState<boolean>(false);
  const [callCount, setCallCount] = useState<boolean>(false);
  const hasLoadedForThisOpenRef = useRef(false);
  const [openApprovalRequiredDrawer, setOpenApprovalRequiredDrawer] =
    useState<boolean>(false);
  const [isOpenBeneList, setIsOpenBeneList] = useState<boolean>(false);
  const [isOpenBulkUploadList, setIsOpenBulkUploadList] =
    useState<boolean>(false);
  const [isOpenPayrollsList, setIsOpenPayrollsList] = useState<boolean>(false);
  const [transactionState, setTransactionState] = useState<TransactionState>(
    initialTransactionState,
  );
  const [callPayByBankList, setCallPayByBankList] = useState<boolean>(false);
  const [searchApproval, setSearchApproval] = useState<string>();
  const debouncedInput = useDebounce(searchApproval || "", 500);
  const [transactionType, setTransactionType] = useState<string | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  const [selectedTransactionList, setSelectedTransactionList] = useState<
    number[]
  >([]);
  const [checkAll, setCheckAll] = useState<boolean>(false);
  const [tableListAll, setTableListAll] = useState<
    ApprovalRequiredTransactions[]
  >([]);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const loader = useRef<HTMLDivElement | null>(null);

  const {
    transactionDetails,
    transactionDetailsLoader,
    openApprovalDrawer,
    setOpenApprovalDrawer,
    selectTransaction,
    openRejectAndNoteModal,
    isReject,
    remark,
    setRemark,
    handleReject,
    submitReject,
    closeRemark,
    handleApprove,
    bulkApproveAndReject,
    isLoading: isActionLoading,
    openSuccessModal,
    successData,
    transactionFailCode,
    closeSuccessModal,
  } = usePaymentApprovalFlow(
    transactionState.openRtpApprovalRequired ? "RTP" : "TRANS",
  );

  const {
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<any>({
    defaultValues: {
      isReject: false,
    },
    resolver: yupResolver(
      yup.object().shape({
        isReject: yup.boolean(),
        isSendForRepair: yup.boolean(),
        reason: yup.string().when("isReject", {
          is: true,
          then: (schema) => schema.required("Please enter the reject reason"),
          otherwise: (schema) => schema.notRequired(),
        }),
        note: yup.string().when("isSendForRepair", {
          is: true,
          then: (schema) => schema.required("Please enter the repair note"),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    ),
  });

  useEffect(() => {
    let isMounted = true;

    const loadPendingApprovals = async () => {
      setIsLoading(true);
      setIsError(false);
      try {
        const payload: DashboardPendingApprovalViewSummaryPayload = {
          pageNo: 1,
          pageSize: 5,
          status: PENDING_APPROVAL_STATUS,
        };
        const result = await gxDashboardPendingApprovalViewSummaryApi(payload);
        if (!isMounted) return;

        setItems(gxIsArray(result?.data) ? result.data : []);
      } catch (err) {
        if (isMounted) {
          setItems([]);
          setIsError(true);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadPendingApprovals();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isReviewAllOpen) {
      hasLoadedForThisOpenRef.current = false;
      return;
    }

    let isMounted = true;
    const isInitialLoadForThisOpen = !hasLoadedForThisOpenRef.current;

    const loadReviewAllList = async () => {
      if (isInitialLoadForThisOpen) setIsTableLoading(true);

      try {
        const payload: DashboardPendingApprovalViewSummaryPayload = {
          pageNo: 1,
          pageSize: REVIEW_ALL_PAGE_SIZE,
          status: PENDING_APPROVAL_STATUS,
        };
        const result = await gxDashboardPendingApprovalViewSummaryApi(payload);
        if (!isMounted) return;

        setTableList(
          gxIsArray(result?.data) ? result.data.map(mapToApprovalCardItem) : [],
        );
      } catch (err) {
        if (isMounted) setTableList([]);
      } finally {
        hasLoadedForThisOpenRef.current = true;
        if (isMounted && isInitialLoadForThisOpen) setIsTableLoading(false);
      }
    };

    loadReviewAllList();

    return () => {
      isMounted = false;
    };
  }, [isReviewAllOpen, callCount]);

  const visibleItems = items.slice(0, COMPACT_WIDGET_VISIBLE_COUNT);

  const handleReviewAllClick = () => {
    setIsReviewAllOpen(true);
    onReviewAll?.();
  };

  const handleCloseReviewAll = () => {
    setIsReviewAllOpen(false);
  };

  const handleRowClick = (event: GridRowClickEvent) => {
    selectTransaction(event?.dataItem);
  };

  const handleApprovalDrawerClose = () => {
    setOpenApprovalDrawer(false);
  };

  const onRemarksSubmit = () => {
    submitReject();
  };

  const handleRejectClick = () => {
    handleReject();
    setValue("isReject", true);
  };

  const handleCloseRemark = () => {
    closeRemark();
    reset();
  };

  const handleCloseSuccess = () => {
    closeSuccessModal();
    reset();
  };

  const handleApprovalRequiredDrawer = () => {
    setOpenApprovalRequiredDrawer((prev) => !prev);
  };

  const handleTransactionType = (value: any) => {
    setTransactionType(value);
  };

  const handleCountry = (value: any) => {
    setTransactionType(null);
    setCountry(value);
  };

  const getApprovalRequiredAllListApi = async (pageToFetch: number) => {
    if (loading) return;
    setLoading(true);

    try {
      const payload = {
        pageNo: pageToFetch,
        pageSize: 10,
        sortBy: Constants.ascending,
        status: `${Constants.awaitingApproval}`,
        searchText: debouncedInput,
        ...((transactionType || country) && {
          filterByList: [
            { filterKey: "paymentTypeId", filterValue: transactionType || "" },
            { filterKey: "paymentCountry", filterValue: country || "" },
          ],
        }),
      };

      const result = await gxPaymentPendingViewSummaryApi(payload);

      if (gxIsArray(result.data)) {
        const mergedData =
          pageToFetch === 1
            ? [...result.data]
            : [...tableListAll, ...result.data];

        const uniqueData = [
          ...new Map(mergedData.map((item) => [item.transId, item])).values(),
        ];

        const transform = uniqueData?.map((item) => ({
          ...item,
          shortForm: item?.debitAccountName
            ?.split(" ")[0]
            ?.toUpperCase()
            ?.slice(0, 3),
          companyName: item?.debitAccountName,
        }));

        setTableListAll(transform);

        if (result.data.length === 10) {
          setPage((prev) => prev + 1);
          setHasMore(true);
        } else {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (err) {
      setHasMore(false);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!openApprovalRequiredDrawer) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !loading && hasMore) {
        getApprovalRequiredAllListApi(page);
      }
    });

    const current = loader.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, [page, loading, hasMore, openApprovalRequiredDrawer]);

  useEffect(() => {
    if (!openApprovalRequiredDrawer) return;
    getApprovalRequiredAllListApi(1);
  }, [openApprovalRequiredDrawer]);

  useEffect(() => {
    if (!openApprovalRequiredDrawer) return;
    setTableListAll([]);
    setPage(1);
    setHasMore(true);
    setLoading(false);
  }, [debouncedInput, transactionType, country]);

  useEffect(() => {
    if (openApprovalRequiredDrawer === false) {
      setSearchApproval(undefined);
      setTransactionType(null);
      setCountry(null);
      setCallCount(true);
      setTableListAll([]);
      setPage(1);
      setHasMore(true);
      setLoading(false);
    }
  }, [openApprovalRequiredDrawer]);

  // Bulk approve/reject reused straight from the hook.
  const handleBulkApprove = () =>
    bulkApproveAndReject(selectedTransactionList, "A");
  const handleBulkReject = () =>
    bulkApproveAndReject(selectedTransactionList, "R");

  useEffect(() => {
    if (openSuccessModal === false) {
      setTableListAll([]);
      setPage(1);
      setHasMore(true);
      setLoading(false);
      setCallCount(true);
    }
  }, [openSuccessModal]);

  const handleRtpInitiatedPaymentDrawer = () => {
    setTransactionState((prev) => ({
      ...prev,
      openRtpApprovalRequired: !prev.openRtpApprovalRequired,
    }));
  };

  const handleAction = (value: any) => {
    if (value.id === "2") {
      handleApprovalRequiredDrawer();
    } else if (value.id === "1") {
      setIsOpenBeneList(true);
    } else if (value.id === "9") {
      router("/dashboard/admin-menu/user-access-request");
    } else if (value.id === "11") {
      setIsOpenBulkUploadList(true);
    } else if (value.id === "12") {
      setIsOpenPayrollsList(true);
    } else if (value.id === "13") {
      setTransactionState((prev) => ({
        ...prev,
        openRtpApprovalRequired: true,
      }));
    }
  };

  const renderApprovalContent = () => {
    if (isError) {
      return (
        <NoDataFound message="Something went wrong. Please try again later." />
      );
    }

    if (visibleItems.length === 0) {
      return <NoDataFound message="No Approval Required Found" />;
    }

    return (
      <div className="gx-compact-widget__list">
        {visibleItems.map((item) => {
          const formatted = gxCurrencyFormat(
            Number(item.debitAmount) || 0,
            item.debitCurrency || "",
            undefined,
            { showSymbol: true },
          );

          return (
            <div className="gx-approval-compact-row" key={item.recordId}>
              <div className="gx-approval-compact-row__main">
                <p className="gx-approval-compact-row__title gx-p14-b">
                  {item.accountName} &middot; {item.bankName}
                </p>
                <p className="gx-approval-compact-row__subtitle">
                  {item.paymentDate} &middot; {item.gxChnNo}
                </p>
              </div>

              <div className="gx-approval-compact-row__amount">
                <span className="gx-compact-amount__symbol gx-p14-r">
                  {formatted.symbol}
                </span>
                <span className="gx-compact-amount__integer gx-p14-b">
                  {formatted.integer}
                </span>
                {formatted.decimal && (
                  <span className="gx-compact-amount__decimal gx-p14-b">
                    {formatted.decimal}
                  </span>
                )}
              </div>

              <div>
                <ApproverCell
                  dataItem={{
                    numberOfLevels: item.noOfApprLevel,
                    numberOfApproved: item.noOfApproved,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="gx-compact-widget gx-compact-widget--skeleton">
        <div className="gx-bene-form-container-loading-overlay">
          <SubmitLoading />
        </div>
      </div>
    );
  }

  return (
    <div className="gx-compact-widget">
      <h3 className="gx-compact-widget__title">Approval Required</h3>

      {renderApprovalContent()}

      <button
        type="button"
        className="gx-compact-widget__review-all"
        onClick={handleReviewAllClick}
      >
        {t("review_all")}
      </button>

      <ApprovalRequiredReviewAllModal
        open={isReviewAllOpen}
        onClose={handleCloseReviewAll}
        isLoading={isTableLoading}
        tableList={tableList}
        onRowClick={handleRowClick}
        callCount={callCount}
        setCallCount={setCallCount}
        onSelect={handleAction}
      />

      {openApprovalDrawer && (
        <DrawerNoFooter
          title={tHook("approval")}
          subtitle={tHook("request")}
          openDrawer={openApprovalDrawer}
          onClose={handleApprovalDrawerClose}
        >
          <>
            <DetailsComponent
              transactionDetailsLoader={transactionDetailsLoader}
              transaction={transactionDetails}
              handleReject={handleRejectClick}
              handleApprove={handleApprove}
              hideFooter={!access.approve}
              schedule={false}
              showEdit={
                transactionDetails?.paymentStatusCode ===
                Constants.pendingConsentAuthorization
              }
              fromRtp={transactionState.openRtpApprovalRequired}
            />

            <ErrorModal
              errorCode={transactionFailCode}
              close={handleCloseSuccess}
              open={!!transactionFailCode}
              module={"Payment"}
            />
          </>
        </DrawerNoFooter>
      )}

      {openApprovalRequiredDrawer && (
        <ViewAllApprovalRequired
          openApprovalRequiredDrawer={openApprovalRequiredDrawer}
          transactionType={transactionType}
          country={country}
          searchApproval={searchApproval}
          selectedTransactionList={selectedTransactionList}
          tableListAll={tableListAll}
          checkAll={checkAll}
          loader={loader}
          loading={loading}
          hasMore={hasMore}
          handleApprovalRequiredDrawer={handleApprovalRequiredDrawer}
          handleTransactionType={handleTransactionType}
          handleCountry={handleCountry}
          setSearchApproval={setSearchApproval}
          setSelectedTransactionList={setSelectedTransactionList}
          handleRowClick={handleRowClick}
          setCheckAll={setCheckAll}
          handleBulkReject={handleBulkReject}
          handleBulkApprove={handleBulkApprove}
        />
      )}

      <BeneficiaryApproval
        setIsOpenBeneList={setIsOpenBeneList}
        isOpenBeneList={isOpenBeneList}
        access={access}
        callCount={callCount}
        setCallCount={setCallCount}
      />

      <BulkUploadApprove
        setIsOpenBulkList={setIsOpenBulkUploadList}
        isOpenBulkList={isOpenBulkUploadList}
        access={access}
        setCallCount={setCallCount}
      />

      <BulkUploadApprovePayroll
        setIsOpenBulkListPayroll={setIsOpenPayrollsList}
        isOpenBulkListPayroll={isOpenPayrollsList}
        access={access}
        setCallCount={setCallCount}
      />

      {transactionState.openRtpApprovalRequired && (
        <RtpPaymentsApproval
          openRtpInitiatedPayment={transactionState.openRtpApprovalRequired}
          handleRtpInitiatedPaymentDrawer={handleRtpInitiatedPaymentDrawer}
          handleRowClick={handleRowClick}
          checkAll={checkAll}
          selectedTransactionList={selectedTransactionList}
          setSelectedTransactionList={setSelectedTransactionList}
          setCheckAll={setCheckAll}
          handleBulkReject={handleBulkReject}
          handleBulkApprove={handleBulkApprove}
          callPayByBankList={callPayByBankList}
          setCallPayByBankList={setCallPayByBankList}
        />
      )}

      <RemarksModal
        errors={errors}
        setValue={setValue}
        value={remark}
        isReject={isReject}
        isOpen={openRejectAndNoteModal}
        onClose={handleCloseRemark}
        onChange={setRemark}
        submit={handleSubmit(onRemarksSubmit)}
      />

      {successData && openSuccessModal && (
        <SuccessModalSM
          onClose={handleCloseSuccess}
          isOpen={openSuccessModal}
          successData={successData}
          successMessage={undefined}
        />
      )}

      {isActionLoading && <SubmitLoading />}
    </div>
  );
};

export default ApprovalRequiredCompactWidget;
